import { useEffect } from "react";
import { Field, useField, useForm } from "react-final-form";
import { useIntl } from "react-intl";
import Section from "../../../common/section";
import * as Validations from "../../../common/form/validator";
import { IconCheckBox, TextInput } from "../../../common/fields";
import Label from "../../../common/fields/Label";
import { TooltipTarget } from "../../../common/tooltipButton";

// Owner rule: Create, Update and Delete inherit Read. A role that can write
// to an area can always see it (RoleHelper.checkRoleScope already treats a
// write as a Read pass for menus and route gating); this keeps the form in
// step with that rule by ticking Read for an area whenever Create, Update
// or Delete is ticked in it, and keeping Read ticked and disabled while any
// of them stays ticked.
const ReadAccessField = ({ area, disabled }) => {
  const intl = useIntl();
  const form = useForm();
  const fieldName = (access) => `scopes['${area}'].${access}`;
  const createValue = useField(fieldName("Create"), {
    subscription: { value: true },
  }).input.value;
  const updateValue = useField(fieldName("Update"), {
    subscription: { value: true },
  }).input.value;
  const deleteValue = useField(fieldName("Delete"), {
    subscription: { value: true },
  }).input.value;
  const readValue = useField(fieldName("Read"), {
    subscription: { value: true },
  }).input.value;

  const impliedByWrite = Boolean(createValue || updateValue || deleteValue);

  useEffect(() => {
    if (impliedByWrite && !readValue) {
      form.change(fieldName("Read"), true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [impliedByWrite, readValue, area]);

  return (
    <TooltipTarget
      tooltip={
        (impliedByWrite &&
          intl.formatMessage({ id: "read_included_with_write" })) ||
        ""
      }
    >
      <Field
        name={fieldName("Read")}
        component={IconCheckBox}
        checkedIcon="pi-eye"
        unCheckIcon="pi-eye"
        type="checkbox"
        disabled={disabled || impliedByWrite}
      />
    </TooltipTarget>
  );
};

const RoleDetailsForm = ({ disabled = false }) => {
  const intl = useIntl();
  return (
    <div className="flex flex-column gap-4">
      <Section title="basic_info">
        <div className="col-12 md:col-6 lg:col-4">
          <Field
            name="name"
            component={TextInput}
            type="text"
            label={intl.formatMessage({
              id: "name",
            })}
            disabled={disabled}
            placeholder={intl.formatMessage({
              id: "name",
            })}
            required
            validate={Validations.composeValidators(Validations.required)}
          />
        </div>
      </Section>
      <Section title="permissions">
        <div className="col-12">
          <div className="flex flex-row gap-6 flex-wrap">
            <div className="flex flex-column gap-2">
              <Label label={intl.formatMessage({ id: "user_scope" })} />
              <div className="flex gap-4">
                <ReadAccessField area="UserAccess" disabled={disabled} />
                <Field
                  name="scopes['UserAccess'].Create"
                  component={IconCheckBox}
                  checkedIcon="pi-plus"
                  unCheckIcon="pi-plus"
                  type="checkbox"
                  disabled={disabled}
                />
                <Field
                  name="scopes['UserAccess'].Update"
                  component={IconCheckBox}
                  checkedIcon="pi-pencil"
                  unCheckIcon="pi-pencil"
                  type="checkbox"
                  disabled={disabled}
                />
                <Field
                  name="scopes['UserAccess'].Delete"
                  component={IconCheckBox}
                  checkedIcon="pi-trash"
                  unCheckIcon="pi-trash"
                  type="checkbox"
                  disabled={disabled}
                />
              </div>
            </div>
            <div className="flex flex-column gap-2">
              <Label label={intl.formatMessage({ id: "enterprise_scope" })} />
              <div className="flex gap-4">
                <ReadAccessField area="EnterpriseAccess" disabled={disabled} />
                <Field
                  name="scopes['EnterpriseAccess'].Create"
                  component={IconCheckBox}
                  checkedIcon="pi-plus"
                  unCheckIcon="pi-plus"
                  type="checkbox"
                  disabled={disabled}
                />
                <Field
                  name="scopes['EnterpriseAccess'].Update"
                  component={IconCheckBox}
                  checkedIcon="pi-pencil"
                  unCheckIcon="pi-pencil"
                  type="checkbox"
                  disabled={disabled}
                />
                <Field
                  name="scopes['EnterpriseAccess'].Delete"
                  component={IconCheckBox}
                  checkedIcon="pi-trash"
                  unCheckIcon="pi-trash"
                  type="checkbox"
                  disabled={disabled}
                />
              </div>
            </div>
            <div className="flex flex-column gap-2">
              <Label label={intl.formatMessage({ id: "content_scope" })} />
              <div className="flex gap-4">
                <ReadAccessField area="ContentAccess" disabled={disabled} />
                <Field
                  name="scopes['ContentAccess'].Create"
                  component={IconCheckBox}
                  checkedIcon="pi-plus"
                  unCheckIcon="pi-plus"
                  type="checkbox"
                  disabled={disabled}
                />
                <Field
                  name="scopes['ContentAccess'].Update"
                  component={IconCheckBox}
                  checkedIcon="pi-pencil"
                  unCheckIcon="pi-pencil"
                  type="checkbox"
                  disabled={disabled}
                />
                <Field
                  name="scopes['ContentAccess'].Delete"
                  component={IconCheckBox}
                  checkedIcon="pi-trash"
                  unCheckIcon="pi-trash"
                  type="checkbox"
                  disabled={disabled}
                />
              </div>
            </div>
            <div className="flex flex-column gap-2">
              <Label label={intl.formatMessage({ id: "report_scope" })} />
              <div className="flex gap-4">
                <ReadAccessField area="ReportAccess" disabled={disabled} />
                <Field
                  name="scopes['ReportAccess'].Create"
                  component={IconCheckBox}
                  checkedIcon="pi-plus"
                  unCheckIcon="pi-plus"
                  type="checkbox"
                  disabled={disabled}
                />
                <Field
                  name="scopes['ReportAccess'].Update"
                  component={IconCheckBox}
                  checkedIcon="pi-pencil"
                  unCheckIcon="pi-pencil"
                  type="checkbox"
                  disabled={disabled}
                />
                <Field
                  name="scopes['ReportAccess'].Delete"
                  component={IconCheckBox}
                  checkedIcon="pi-trash"
                  unCheckIcon="pi-trash"
                  type="checkbox"
                  disabled={disabled}
                />
              </div>
            </div>
          </div>
        </div>
      </Section>
    </div>
  );
};

export default RoleDetailsForm;
